import { useEffect } from 'react';

import useNoRenderRef from './use-no-render-ref';

/**
 * Keep in sync with a value as it changes. This is useful if you want one piece of state to sync with
 * another piece of state for a limited time, e.g. before a user interacts with an element keep the local
 * state in sync with state from the DB.
 *
 * If your intention is to sync local state with DB state this should only be used if the syncing is ALWAYS
 * turned off AFTER the state from the DB has had a chance to load.
 * @param value The value you want to keep in sync with
 * @param onChange Any callback function that should receive the new value whenever it changes. This accepts setState.
 * @param sync While true the callback function will be invoked whenever the value changes
 */
export default function useSyncWhile<T>(value: T, onChange: (value: T) => void, sync: boolean): void {
  const onChangeRef = useNoRenderRef(onChange);

  useEffect(() => {
    // Before the user has edited the field it should reflect whatever the value of the field is.
    if (sync) onChangeRef.current(value);
  }, [sync, value, onChangeRef]);
}
